import React from "react";
import { Card } from "../../ui/Card/Card";
import { Badge } from "../../ui/Badge/Badge";
import { Text } from "../../ui/Text/Text";
import { Button } from "../../ui/Button/Button";
import { cn } from "../../../lib/cn";
import {
  Eye,
  Package,
  Users as UsersIcon,
  AlertTriangle,
  Edit,
} from "lucide-react";
import "@ui5/webcomponents-icons/dist/customer.js";
import "@ui5/webcomponents/dist/Icon.js";

/**
 * CustomerCard Component
 * Displays customer information in a card format for selection grid
 *
 * @param {Object} props - Component props
 * @param {Object} props.customer - Customer object
 * @param {string} props.customer.id - Customer ID
 * @param {string} props.customer.name - Customer name
 * @param {string} [props.customer.description] - Customer description
 * @param {string} [props.customer.contactEmail] - Contact email
 * @param {string} [props.customer.contactPerson] - Contact person name
 * @param {string} [props.customer.imageUrl] - Customer profile image URL
 * @param {number} [props.serviceCount=0] - Number of services assigned to customer
 * @param {Array} [props.consultants=[]] - Array of consultant objects {id, firstName, lastName}
 * @param {number} [props.pendingImpacts=0] - Number of pending change impacts
 * @param {Function} props.onClick - Click handler
 * @param {Function} [props.onViewDetails] - View details action handler
 * @param {Function} [props.onAssignServices] - Assign services action handler
 * @param {Function} [props.onEdit] - Edit customer handler (admin/consultant only)
 * @param {boolean} [props.selected] - Whether this customer is selected
 * @param {boolean} [props.showActions=false] - Whether to show quick action buttons
 * @param {string} [props.className] - Additional CSS classes
 */
export function CustomerCard({
  customer,
  onClick,
  selected = false,
  serviceCount = 0,
  consultants = [],
  pendingImpacts = 0,
  onViewDetails,
  onAssignServices,
  onEdit,
  showActions = false,
  className,
}) {
  return (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1",
        "border-2",
        selected ? "border-blue-500 bg-blue-50" : "border-transparent",
        className
      )}
      onClick={() => onClick?.(customer)}
    >
      <div className="p-6">
        {/* Customer Image and Name */}
        <div className="flex items-start gap-4 mb-3">
          {/* Profile Image */}
          <div className="flex-shrink-0">
            {customer.imageUrl ? (
              <img
                src={customer.imageUrl}
                alt={customer.name}
                className="w-16 h-16 rounded-lg object-cover object-center"
                style={{ objectFit: "cover", objectPosition: "center" }}
                onError={(e) => {
                  // Fallback if image fails to load
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className={cn(
                "w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold shadow-md",
                customer.imageUrl ? "hidden" : "flex"
              )}
            >
              {customer.name ? customer.name.charAt(0).toUpperCase() : "?"}
            </div>
          </div>

          {/* Customer Name */}
          <div className="flex-1 min-w-0">
            <Text className="text-xl font-bold text-gray-900 truncate">
              {customer.name}
            </Text>
          </div>

          {/* Edit Button (Admin/Consultant only) */}
          {onEdit && (
            <Button
              design="Transparent"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(customer);
              }}
              className="flex-shrink-0"
              title="Edit customer"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Description */}
        {customer.description && (
          <div className="mb-4">
            <Text className="text-sm text-gray-600 line-clamp-2">
              {customer.description}
            </Text>
          </div>
        )}

        {/* Stats Section - Only show if data is provided */}
        {(serviceCount > 0 || consultants.length > 0 || pendingImpacts > 0) && (
          <div className="mb-4 space-y-2">
            {/* Service Count */}
            {serviceCount > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-gray-700">
                  {serviceCount}{" "}
                  {serviceCount === 1 ? "Active Service" : "Active Services"}
                </span>
              </div>
            )}

            {/* Consultants */}
            {consultants.length > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <UsersIcon className="h-4 w-4 text-green-600" />
                <div className="flex items-center gap-1">
                  <span className="text-gray-700">
                    {consultants.slice(0, 2).map((c, i) => (
                      <span key={c.id}>
                        {c.firstName} {c.lastName}
                        {i < Math.min(consultants.length, 2) - 1 && ", "}
                      </span>
                    ))}
                  </span>
                  {consultants.length > 2 && (
                    <span className="text-xs text-gray-500">
                      +{consultants.length - 2} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Pending Impacts Warning */}
            {pendingImpacts > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="font-medium text-yellow-700">
                  {pendingImpacts} Pending{" "}
                  {pendingImpacts === 1 ? "Impact" : "Impacts"}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        {showActions && (
          <div className="mb-4 flex gap-2 flex-wrap">
            {onViewDetails && (
              <Button
                size="small"
                design="Transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDetails(customer);
                }}
                className="flex items-center gap-1"
              >
                <Eye className="h-3 w-3" />
                View
              </Button>
            )}
            {onAssignServices && (
              <Button
                size="small"
                design="Transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignServices(customer);
                }}
                className="flex items-center gap-1"
              >
                <Package className="h-3 w-3" />
                Assign
              </Button>
            )}
          </div>
        )}

        {/* Footer with Badge */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <Badge design={selected ? "Information" : "Positive"}>
            {selected ? "Selected" : "Available"}
          </Badge>

          {pendingImpacts > 0 && (
            <Badge design="Attention" className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {pendingImpacts}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
}
